import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";
import { api, formatEUR } from "../lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const FREE_THRESHOLD = 100;
const SHIPPING = 4.95;

export default function CartSheet() {
  const { items, isOpen, setIsOpen, updateQuantity, removeItem, subtotal, count } = useCart();
  const [loading, setLoading] = useState(false);

  const shippingCost = subtotal >= FREE_THRESHOLD || subtotal === 0 ? 0 : SHIPPING;
  const total = subtotal + shippingCost;
  const remaining = Math.max(0, FREE_THRESHOLD - subtotal);
  const progress = Math.min(100, (subtotal / FREE_THRESHOLD) * 100);

  const handleCheckout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const res = await api.post("/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      console.error(err);
      toast.error("No se pudo iniciar el checkout. Inténtalo de nuevo.");
      setLoading(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        data-testid="cart-sheet"
        className="w-full sm:max-w-md bg-[color:var(--brand-bg)] border-l border-[color:var(--brand-border)] flex flex-col p-0"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[color:var(--brand-border)]">
          <SheetTitle className="font-serif text-2xl text-[color:var(--brand-primary)]">
            Tu cesta ({count})
          </SheetTitle>
          <SheetDescription className="text-xs text-[color:var(--brand-neutral)]/70">
            Mermeladas artesanas de Mimes Gourmet.
          </SheetDescription>
        </SheetHeader>

        {items.length === 0 ? (
          <div data-testid="cart-empty" className="flex-1 flex flex-col items-center justify-center px-8 text-center gap-4">
            <ShoppingBag className="w-12 h-12 text-[color:var(--brand-sage)]" strokeWidth={1.2} />
            <p className="font-serif text-xl text-[color:var(--brand-primary)]">Tu cesta está vacía</p>
            <p className="text-sm text-[color:var(--brand-neutral)]/70">
              Descubre nuestros sabores del Pirineo y añádelos a tu cesta.
            </p>
            <Link to="/tienda" onClick={() => setIsOpen(false)}>
              <Button data-testid="cart-go-shop" className="bg-[color:var(--brand-primary)] hover:bg-black text-white rounded-md">
                Ir a la tienda
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <div className="px-6 py-4 border-b border-[color:var(--brand-border)]">
              {subtotal < FREE_THRESHOLD ? (
                <>
                  <p className="text-xs text-[color:var(--brand-neutral)]">
                    Añade <span className="font-semibold text-[color:var(--brand-primary)]">{formatEUR(remaining)}</span> más para envío gratuito.
                  </p>
                  <div className="mt-2 h-1.5 bg-[color:var(--brand-bg-alt)] rounded-full overflow-hidden">
                    <div className="h-full bg-[color:var(--brand-sage)] transition-all duration-500" style={{ width: `${progress}%` }} />
                  </div>
                </>
              ) : (
                <p className="text-xs font-semibold text-[color:var(--brand-sage)]">
                  ✓ Envío gratuito desbloqueado
                </p>
              )}
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
              {items.map((item) => (
                <div key={item.product_id} data-testid={`cart-item-${item.product_id}`} className="flex gap-4">
                  <div className="w-20 h-20 rounded-md overflow-hidden bg-[color:var(--brand-bg-alt)] shrink-0">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <div>
                        <p className="font-serif text-lg leading-tight text-[color:var(--brand-primary)]">{item.name}</p>
                        <p className="text-xs text-[color:var(--brand-neutral)]/60 mt-0.5">{item.weight_g}g · {formatEUR(item.price_eur)}</p>
                      </div>
                      <button
                        data-testid={`cart-remove-${item.product_id}`}
                        onClick={() => removeItem(item.product_id)}
                        className="text-[color:var(--brand-neutral)]/50 hover:text-[color:var(--brand-terracotta)] transition"
                        aria-label="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center border border-[color:var(--brand-border)] rounded-md">
                        <button
                          data-testid={`cart-qty-dec-${item.product_id}`}
                          onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                          className="p-1.5 hover:bg-[color:var(--brand-bg-alt)]"
                          aria-label="Reducir"
                        >
                          <Minus className="w-3 h-3" strokeWidth={2} />
                        </button>
                        <span className="px-3 text-sm min-w-8 text-center" data-testid={`cart-qty-${item.product_id}`}>
                          {item.quantity}
                        </span>
                        <button
                          data-testid={`cart-qty-inc-${item.product_id}`}
                          onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                          className="p-1.5 hover:bg-[color:var(--brand-bg-alt)]"
                          aria-label="Aumentar"
                        >
                          <Plus className="w-3 h-3" strokeWidth={2} />
                        </button>
                      </div>
                      <span className="font-medium text-sm text-[color:var(--brand-primary)]">
                        {formatEUR(item.price_eur * item.quantity)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-[color:var(--brand-border)] px-6 py-5 space-y-3 bg-[color:var(--brand-bg-alt)]/50">
              <div className="flex justify-between text-sm">
                <span className="text-[color:var(--brand-neutral)]">Subtotal <span className="text-[10px] text-[color:var(--brand-neutral)]/60">(IVA incl.)</span></span>
                <span data-testid="cart-subtotal" className="font-medium text-[color:var(--brand-primary)]">
                  {formatEUR(subtotal)}
                </span>
              </div>
              <div className="flex justify-between text-[11px] text-[color:var(--brand-neutral)]/70 -mt-1">
                <span>de los cuales IVA (10%)</span>
                <span data-testid="cart-vat">{formatEUR(subtotal - subtotal / 1.10)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[color:var(--brand-neutral)]">Envío estimado</span>
                <span className="text-[color:var(--brand-primary)]">
                  {shippingCost === 0 ? "Gratis" : formatEUR(shippingCost)}
                </span>
              </div>
              <div className="flex justify-between pt-2 border-t border-[color:var(--brand-border)]">
                <span className="font-serif text-lg text-[color:var(--brand-primary)]">Total</span>
                <span data-testid="cart-total" className="font-serif text-lg font-semibold text-[color:var(--brand-primary)]">
                  {formatEUR(total)}
                </span>
              </div>
              <div data-testid="cart-pickup-note" className="mt-2 p-2.5 rounded-md bg-[color:var(--brand-sage)]/10 border border-[color:var(--brand-sage)]/30">
                <p className="text-[11px] text-[color:var(--brand-primary)] leading-relaxed">
                  <span className="font-semibold">¿Vives cerca de Graus?</span> Elige <span className="font-semibold">Recogida en Graus (gratis)</span> en el checkout y ahorra el envío.
                </p>
              </div>
              <p className="text-[10px] text-[color:var(--brand-neutral)]/60">Precios con IVA (10%) incluido. Envío final calculado en el checkout según opción elegida.</p>
              <Button
                data-testid="cart-checkout-btn"
                onClick={handleCheckout}
                disabled={loading}
                className="w-full bg-[color:var(--brand-primary)] hover:bg-black text-white rounded-md py-6 font-medium tracking-wide"
              >
                {loading ? "Redirigiendo…" : "Ir al pago"}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
